import type { JSONContent } from "@tiptap/core";
import { PURCHASER_API_BASE_URL, getHeaders } from "../Constants";
import type { FileUpload } from "./FileUpload.ts";

export enum GetRFQTemplatesSortBy {
    NAME = "name",
    UPDATED_AT = "updatedAt",
}

export enum SortOrder {
    ASC = "asc",
    DESC = "desc",
}

export type TemplateCreatedByUser = {
    id: number;
    first_name?: string;
    last_name?: string;
    email: string;
};

export type RFQTemplateAttachment = {
    id: number;
    createdAt: string;
    updatedAt: string;

    // Relationships
    fileId: number;
    file: FileUpload;
    reminderId: number;
    reminder: RFQTemplate;
};

export type RFQTemplate = {
    id: number;
    name: string;
    content: string;
    contentJson: JSONContent;
    createdAt: string;
    updatedAt: string;
    user: TemplateCreatedByUser;
    attachments: RFQTemplateAttachment[];
};

export default {
    getRFQTemplates: (search?: string, sortBy?: GetRFQTemplatesSortBy, sortOrder?: SortOrder) => {
        const qsArray: string[] = [];
        if (search) {
            qsArray.push(`search=${search}`);
        }
        if (sortBy) {
            qsArray.push(`sortBy=${sortBy}`);
        }
        if (sortOrder) {
            qsArray.push(`sortOrder=${sortOrder}`);
        }
        const querystring = qsArray.length > 0 ? `?${qsArray.join("&")}` : "";

        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/templates${querystring}`, {
            method: "GET",
            headers: getHeaders(),
        }).then(async res => {
            if (!res.ok) {
                throw new Error("Failed to fetch RFQ templates");
            }
            const json = await res.json();
            return json as RFQTemplate[];
        });
    },

    getRFQTemplate: (id: number | string) =>
        fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/templates/${id}`, {
            method: "GET",
            headers: getHeaders(),
        }).then(async res => {
            if (!res.ok) {
                throw new Error("Failed to fetch RFQ template");
            }
            const json = await res.json();
            return json as RFQTemplate;
        }),

    createRFQTemplate: (name: string, contentJson: JSONContent) =>
        fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/templates`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({
                name,
                contentJson,
            }),
        }).then(async res => {
            if (!res.ok) {
                throw new Error("Failed to create RFQ template");
            }
            const json = await res.json();
            return json as RFQTemplate;
        }),

    updateRFQTemplate: (id: number | string, name?: string, contentJson?: JSONContent) =>
        fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/templates/${id}`, {
            method: "PATCH",
            headers: getHeaders(),
            body: JSON.stringify({
                name,
                contentJson,
            }),
        }).then(async res => {
            if (!res.ok) {
                throw new Error("Failed to update RFQ template");
            }
            const json = await res.json();
            return json as RFQTemplate;
        }),

    deleteRFQTemplate: (id: number) =>
        fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/templates/${id}`, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({}),
        }).then(async res => {
            if (!res.ok) {
                throw new Error("Failed to delete RFQ template");
            }
            return res;
        }),

    addFilesToRFQTemplate: (id: number, fileIds: number[]) =>
        fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/templates/${id}/attachments`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ fileIds }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to add files to RFQ template");
            }
            return res.json();
        }),

    removeFileFromRFQTemplate: (id: number, attachmentId: number) =>
        fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/templates/${id}/attachments/${attachmentId}`, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({}),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to remove file from RFQ template");
            }
            return res.json();
        }),
};
